import React, { useState } from 'react';
import axios from '../../config/axiosConfig';
import { MdArrowBack, MdTranslate, MdAutoAwesome } from 'react-icons/md';
import { SUPPORTED_LANGUAGES, translateText } from '../../utils/translator';

const AddNaiNews = ({ onBack, onNewsAdded }) => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [translations, setTranslations] = useState({});
    const [activeLangTab, setActiveLangTab] = useState('ta');
    const [translatingTab, setTranslatingTab] = useState(false);
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null);

    const handleImageChange = (e) => {
        if (e.target.files) {
            setImages(Array.from(e.target.files));
        }
    };

    const handleTranslationChange = (langCode, field, value) => {
        setTranslations((prev) => ({
            ...prev,
            [langCode]: {
                ...prev[langCode],
                [field]: value,
            },
        }));
    };

    const handleAutoTranslateCurrentTab = async () => {
        if (!title && !description) {
            alert("Please enter title or description first before auto-translating.");
            return;
        }

        try {
            setTranslatingTab(true);
            const [transTitle, transDesc] = await Promise.all([
                title ? translateText(title, activeLangTab) : Promise.resolve(''),
                description ? translateText(description, activeLangTab) : Promise.resolve(''),
            ]);

            setTranslations((prev) => ({
                ...prev,
                [activeLangTab]: {
                    title: transTitle,
                    description: transDesc,
                },
            }));
        } catch (err) {
            console.error("Auto-translate error:", err);
            alert("Failed to auto-translate. Please try again or type manually.");
        } finally {
            setTranslatingTab(false);
        }
    };

    const handleClearTranslation = (langCode) => {
        setTranslations((prev) => {
            const updated = { ...prev };
            delete updated[langCode];
            return updated;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setStatus(null);

        const formData = new FormData();
        formData.append('title', title);
        formData.append('description', description);
        formData.append('created_at', date); 
        formData.append('translations', JSON.stringify(translations));
        
        images.forEach((image) => {
            formData.append('images', image);
        });

        try {
             await axios.post('nai-news/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            setLoading(false);
            setStatus({ type: 'success', message: 'News added successfully!' });
            setTimeout(() => {
                onNewsAdded(); 
            }, 1000);
        } catch (error) {
            console.error("Error adding news:", error);
            setLoading(false);
            setStatus({ type: 'error', message: 'Failed to add news.' });
        }
    };

    const currentLangInfo = SUPPORTED_LANGUAGES.find((l) => l.code === activeLangTab) || SUPPORTED_LANGUAGES[0];

    return (
        <div className="bg-white rounded-lg shadow p-6">
            <button onClick={onBack} className="flex items-center text-gray-600 hover:text-gray-900 mb-6">
                <MdArrowBack className="mr-2" /> Back to News List
            </button>

            <h2 className="text-xl font-bold text-gray-800 mb-6">Add New News</h2>

            {status && (
                <div className={`p-4 mb-4 rounded ${status.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {status.message}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Title</label>
                    <input
                        type="text"
                        required
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                        required
                        rows={6}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Publish Date</label>
                    <input
                        type="date"
                        required
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2"
                    />
                </div>

                {/* Optional Multilingual Translations Section */}
                <div className="space-y-4 border border-blue-100 rounded-xl p-5 bg-gradient-to-br from-blue-50/30 via-white to-gray-50/40 shadow-sm">
                    <div className="border-b border-gray-200 pb-2">
                        <h3 className="text-base font-bold text-gray-800 flex items-center">
                            <MdTranslate className="text-blue-600 mr-2" />
                            Multilingual Translations (Optional)
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Pre-populate or customize translations for Tamil, Hindi, Kannada, Telugu, or Malayalam.
                        </p>
                    </div>

                    {/* Language Tabs */}
                    <div className="flex flex-wrap gap-2 pt-1">
                        {SUPPORTED_LANGUAGES.map((lang) => {
                            const isTabActive = activeLangTab === lang.code;
                            const hasCustomData = Boolean(
                                translations[lang.code] &&
                                (translations[lang.code].title?.trim() || translations[lang.code].description?.trim())
                            );

                            return (
                                <button
                                    type="button"
                                    key={lang.code}
                                    onClick={() => setActiveLangTab(lang.code)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all ${
                                        isTabActive
                                            ? 'bg-blue-600 text-white shadow ring-1 ring-blue-400'
                                            : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                                    }`}
                                >
                                    <span>{lang.name}</span>
                                    <span className={isTabActive ? 'text-blue-200' : 'text-gray-400'}>
                                        ({lang.nativeName})
                                    </span>
                                    {hasCustomData && (
                                        <span className={`w-1.5 h-1.5 rounded-full ${isTabActive ? 'bg-green-300' : 'bg-green-500'}`} />
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* Active Tab Panel */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3 shadow-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2 pb-2 border-b border-gray-100">
                            <span className="text-xs font-semibold text-gray-700">
                                {currentLangInfo.name} ({currentLangInfo.nativeName})
                            </span>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={handleAutoTranslateCurrentTab}
                                    disabled={translatingTab}
                                    className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors disabled:opacity-60"
                                >
                                    <MdAutoAwesome className="mr-1 text-blue-600" />
                                    {translatingTab ? 'Translating...' : `Auto-Translate`}
                                </button>
                                {translations[activeLangTab] && (
                                    <button
                                        type="button"
                                        onClick={() => handleClearTranslation(activeLangTab)}
                                        className="text-xs text-red-600 hover:underline"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Title ({currentLangInfo.name})</label>
                            <input
                                type="text"
                                placeholder={`Enter ${currentLangInfo.name} title`}
                                value={translations[activeLangTab]?.title || ''}
                                onChange={(e) => handleTranslationChange(activeLangTab, 'title', e.target.value)}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2 text-sm"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Description ({currentLangInfo.name})</label>
                            <textarea
                                rows={4}
                                placeholder={`Enter ${currentLangInfo.name} description`}
                                value={translations[activeLangTab]?.description || ''}
                                onChange={(e) => handleTranslationChange(activeLangTab, 'description', e.target.value)}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2 text-sm"
                            />
                        </div>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Images</label>
                    <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageChange}
                        className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    {images.length > 0 && (
                        <div className="mt-2 text-sm text-gray-500">
                            {images.length} file(s) selected
                        </div>
                    )}
                </div>

                <div>
                    <button
                        type="submit"
                        disabled={loading}
                        className={`w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {loading ? 'Publishing...' : 'Publish News'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddNaiNews;
